import React, { useMemo } from 'react';
import { CopyTextButton } from './CopyTextButton';
import { buildMonsterPhotoAudit, clsLabel, formatNeededImages, type NeededImage } from '../../utils/monsterPhotoAudit';
import type { WorldData } from '../../types/world';
type Group = {
  key: string;
  title: string;
  note: string;
  rows: NeededImage[];
};

/**
 * Lista depurada de imágenes realmente necesarias para entidades PUBLICADAS (alias aplicados: Pet Trainer 234 no aparece).
 * Agrupada por tipo documentado; las entidades sin evidencia van como "pendientes de clasificación" y no se ocultan.
 */
export function MissingMonsterPhotos({
  w
}: {
  w: WorldData;
}) {
  const audit = useMemo(() => buildMonsterPhotoAudit(w, {}, false), [w]);
  const groups = useMemo<Group[]>(() => {
    const by = (p: number[]) => audit.needed.filter(n => p.includes(n.priority));
    return [{
      key: 'mon',
      title: 'Monstruos publicados sin foto',
      note: 'Confirmados por el staff, con función explícita (misión o invasión documentada) o con al menos dos fuentes del servidor.',
      rows: by([1])
    }, {
      key: 'npc',
      title: 'NPC y objetos sin foto',
      note: 'NPC documentados y objetos clasificados por el staff.',
      rows: by([2, 3])
    }, {
      key: 'pend',
      title: 'Pendientes de clasificación',
      note: 'Sin evidencia de qué son (puertas, estatuas, trampas, cofres, elementos de eventos…). Clasificar antes de pedir la foto.',
      rows: by([4])
    }, {
      key: 'rep',
      title: 'Reemplazos y mejoras',
      note: 'Se conserva la foto actual hasta tener reemplazo. El rótulo visible es presentación, no identidad.',
      rows: by([0, 5, 6])
    }];
  }, [audit]);
  return <div className="mb-6" data-id="element-1547" data-mp="15hrmk150cbib">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3" data-id="element-1548" data-mp="15ijkhim2ulbr">
        <h3 className="text-base font-semibold text-white" data-id="element-1549" data-mp="19ijyx396ewit">
          Imágenes necesarias <span className="font-normal text-gray-400" data-id="element-1550" data-mp="1ej7wk7rwubm0">· {audit.needed.length} de {audit.publishedTotal} entidades publicadas</span>
        </h3>
        <CopyTextButton text={formatNeededImages(audit)} label="Copiar imágenes necesarias" data-id="element-1551" data-mp="1ji2fd9pvo064" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2" data-id="element-1552" data-mp="3fl74ujtfg6di">
        {groups.map(g => <details key={g.key} className="rounded-2xl border border-white/10 p-4" open={g.key === 'mon'} data-id="element-1553" data-mp="3an5q2b31at7u">
            <summary className="cursor-pointer text-sm font-semibold text-white" data-id="element-1554" data-mp="3pcbj1hba3n7a">
              {g.title} <span className="font-normal text-gray-400" data-id="element-1555" data-mp="3kifq4t9vgqiz">· {g.rows.length}</span>
            </summary>
            <p className="mt-1 text-xs text-gray-500" data-id="element-1556" data-mp="24g9a19dugz6">{g.note}</p>
            <ul className="mt-3 max-h-80 space-y-2 overflow-y-auto pr-1 text-sm" data-id="element-1557" data-mp="3ualgxzoe11wl">
              {g.rows.map(r => <li key={`${r.id}-${r.priority}`} data-id="element-1558" data-mp="c2w1k7k5bwa9">
                  <span className="text-gray-200" data-id="element-1559" data-mp="7547b6w13y2w">
                    <span className="font-mono text-xs text-gray-500" data-id="element-1560" data-mp="76f9j29tsxrw">{r.id}</span> {r.name} <span className="text-xs text-gray-500" data-id="element-1561" data-mp="2dusb5bmr6w2e">· {clsLabel[r.cls]}</span>
                  </span>
                  <span className="block text-xs text-gray-500" data-id="element-1562" data-mp="28xiq086ed6kd">{r.reason}</span>
                </li>)}
              {!g.rows.length && <li className="text-gray-500" data-id="element-1563" data-mp="ogj9ud6iwk8q">Ninguno.</li>}
            </ul>
          </details>)}
      </div>
    </div>;
}